import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@node-space/ui-components'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { SidePanelSummary } from 'components/SidePanel/SidePanelSummary'
import type { AddressDetails, BusinessProfileDetails, CompanyDetails } from 'types/customers'
import { formatEnumValue } from '../utils/utils'

const CompanyInfoSection = ({ company }: { company: CompanyDetails }) => {
  const { t } = useTranslation()
  const { name, entityType, registrationNumber, taxIdentification, incorporationDate } = company

  const rows = [
    ...(name ? [{ label: t('customers.businessName'), content: name }] : []),
    ...(entityType
      ? [{ label: t('customers.entityType'), content: formatEnumValue(entityType) }]
      : []),
    ...(registrationNumber
      ? [{ label: t('customers.registrationNumber'), content: registrationNumber }]
      : []),
    ...(taxIdentification?.number
      ? [{ label: t('customers.taxNumber'), content: taxIdentification.number }]
      : []),
    ...(taxIdentification?.taxResidenceCountryCode
      ? [
          {
            label: t('customers.taxResidency'),
            content: taxIdentification.taxResidenceCountryCode,
          },
        ]
      : []),
    ...(incorporationDate
      ? [{ label: t('customers.incorporationDate'), content: incorporationDate }]
      : []),
  ]

  if (!rows.length) return null

  return (
    <SidePanelSection heading={t('customers.companyDetails')}>
      <SidePanelSummary rows={rows} />
    </SidePanelSection>
  )
}

const AddressSection = ({
  address,
  heading,
}: {
  address: AddressDetails | null | undefined
  heading?: string
}) => {
  const { t } = useTranslation()
  if (!address) return null
  return (
    <SidePanelSection heading={heading}>
      <SidePanelSummary
        rows={[
          ...(address.addressLine1
            ? [{ label: t('customers.addressLine1'), content: address.addressLine1 }]
            : []),
          ...(address.city ? [{ label: t('customers.city'), content: address.city }] : []),
          ...(address.postalCode
            ? [{ label: t('customers.postalCode'), content: address.postalCode }]
            : []),
          ...(address.countryCode
            ? [{ label: t('customers.countryCode'), content: address.countryCode }]
            : []),
        ]}
      />
    </SidePanelSection>
  )
}

const BusinessProfileSection = ({
  businessProfile,
  heading,
}: {
  businessProfile: BusinessProfileDetails | null | undefined
  heading?: string
}) => {
  const { t } = useTranslation()
  if (!businessProfile) return null
  const {
    naicsCode,
    website,
    monthlyExpectedVolumes,
    sourceOfFunds,
    intendedUseOfAccount,
    isRegulated,
    regulatorInformation,
  } = businessProfile
  return (
    <SidePanelSection heading={heading}>
      <SidePanelSummary
        rows={[
          ...(naicsCode ? [{ label: t('customers.naicsCode'), content: naicsCode }] : []),
          ...(website ? [{ label: t('customers.website'), content: website }] : []),
          ...(monthlyExpectedVolumes
            ? [
                {
                  label: t('customers.monthlyExpectedVolumes'),
                  content: formatEnumValue(monthlyExpectedVolumes),
                },
              ]
            : []),
          ...(sourceOfFunds
            ? [{ label: t('customers.sourceOfFunds'), content: formatEnumValue(sourceOfFunds) }]
            : []),
          ...(intendedUseOfAccount
            ? [
                {
                  label: t('customers.intendedUse'),
                  content: formatEnumValue(intendedUseOfAccount),
                },
              ]
            : []),
          ...(isRegulated !== undefined && isRegulated !== null
            ? [
                {
                  label: t('embeddedCustomers.businessForm.isRegulated'),
                  content: isRegulated ? t('yes') : t('no'),
                },
              ]
            : []),
          ...(regulatorInformation?.regulatorName
            ? [
                {
                  label: t('customers.regulatorName'),
                  content: regulatorInformation.regulatorName,
                },
              ]
            : []),
          ...(regulatorInformation?.regulatorJurisdiction
            ? [
                {
                  label: t('customers.regulatorJurisdiction'),
                  content: regulatorInformation.regulatorJurisdiction,
                },
              ]
            : []),
        ]}
      />
    </SidePanelSection>
  )
}

export const CustomerCompanyTab = ({ company }: { company: CompanyDetails }) => {
  const { t } = useTranslation()
  const { address, isOperationalAddressDifferent, operationalAddress, businessProfile } = company
  return (
    <Box flexCol gapY={16} padding={16}>
      <CompanyInfoSection company={company} />
      <AddressSection address={address} heading={t('customers.businessAddress')} />
      {isOperationalAddressDifferent && (
        <AddressSection address={operationalAddress} heading={t('customers.operationalAddress')} />
      )}
      <BusinessProfileSection
        businessProfile={businessProfile}
        heading={t('customers.businessProfile')}
      />
    </Box>
  )
}
